/**
 * CompetitorTopPages — nav-redesign row 10 (Competitor Comparison rebuild).
 *
 * A focused, minimal rebuild of the competitor view around the data the meeting
 * actually asked for: the tenant's own domain auto-filled, competitor URLs, an
 * Ahrefs traffic chart of top pages, and an "add to keyword pipeline" action.
 *
 * Deliberately self-contained — it reuses the ALREADY-BUILT (but previously
 * unwired) Ahrefs top-pages backend/service/hook (useTopPagesMutation →
 * /ahrefs/top-pages) rather than touching the 2.3k-LOC CompetitorComparisonPage
 * internals. Mounted as a flag-gated tab (VITE_COMPETITOR_V2).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, Loader2, Plus, X, ExternalLink, TrendingUp } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTopPagesMutation } from '@/hooks/useAhrefsAPI';
import { formatNumber } from '@/services/ahrefsService';
import type { TopPagesResponse } from '@/services/ahrefsService';

interface CompetitorTopPagesProps {
  /** The tenant's own domain (from the tenant store). */
  tenantId: string;
}

const MAX_COMPETITORS = 4;

/** Strip protocol/path so the Ahrefs target + display is a bare domain. */
function toDomain(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '');
}

export function CompetitorTopPages({ tenantId }: CompetitorTopPagesProps) {
  const navigate = useNavigate();
  const topPages = useTopPagesMutation();

  const [ownUrl, setOwnUrl] = useState(tenantId || '');
  const [competitors, setCompetitors] = useState<string[]>(['']);
  const [results, setResults] = useState<Record<string, TopPagesResponse>>({});
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const targets = useMemo(() => {
    const list = [ownUrl, ...competitors].map(toDomain).filter(Boolean);
    return Array.from(new Set(list)); // dedupe
  }, [ownUrl, competitors]);

  const ownDomain = toDomain(ownUrl);

  const analyze = async () => {
    if (!targets.length) {
      setError('Enter at least your own site URL.');
      return;
    }
    setError(null);
    setAnalyzing(true);
    const next: Record<string, TopPagesResponse> = {};
    for (const target of targets) {
      try {
        next[target] = await topPages.mutateAsync({ target, limit: 20 });
      } catch {
        // Per-target failure shouldn't abort the whole run; skip and continue.
      }
    }
    setResults(next);
    if (!Object.keys(next).length) {
      setError('No results — check the URLs, or Ahrefs may not have data for these domains.');
    }
    setAnalyzing(false);
  };

  const addToPipeline = (keyword?: string) => {
    if (!keyword) return;
    navigate(`/content/planner?open=true&keyword=${encodeURIComponent(keyword)}&step=keywords`);
  };

  const setCompetitor = (i: number, val: string) =>
    setCompetitors((prev) => prev.map((c, idx) => (idx === i ? val : c)));

  const ownChartData = useMemo(() => {
    const own = results[ownDomain];
    if (!own) return [];
    return [...own.pages]
      .sort((a, b) => b.traffic - a.traffic)
      .slice(0, 10)
      .map((p) => ({
        // short label: last path segment or host
        label: (p.url.replace(/\/$/, '').split('/').pop() || p.url).slice(0, 22),
        url: p.url,
        traffic: p.traffic,
      }));
  }, [results, ownDomain]);

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <Card className="border-0 shadow-sm p-5 space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Your site
          </label>
          <Input
            value={ownUrl}
            onChange={(e) => setOwnUrl(e.target.value)}
            placeholder="yourdomain.com"
            className="max-w-md"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Competitors
          </label>
          <div className="space-y-2">
            {competitors.map((c, i) => (
              <div key={i} className="flex items-center gap-2 max-w-md">
                <Input
                  value={c}
                  onChange={(e) => setCompetitor(i, e.target.value)}
                  placeholder="competitor.com"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCompetitors((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label="Remove competitor"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {competitors.length < MAX_COMPETITORS && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCompetitors((prev) => [...prev, ''])}
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" /> Add competitor
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={analyze} disabled={analyzing} className="gap-1.5 bg-coral hover:bg-coral-hover text-white">
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
            {analyzing ? 'Analyzing…' : 'Analyze top pages'}
          </Button>
          {error && <span className="text-sm text-destructive">{error}</span>}
        </div>
      </Card>

      {/* Own-site traffic chart */}
      {ownChartData.length > 0 && (
        <Card className="border-0 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-coral" />
            <h3 className="font-semibold text-foreground">Top pages by traffic — {ownDomain}</h3>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ownChartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
                <ReTooltip
                  formatter={(v: number) => [`${v.toLocaleString()} visits/mo`, 'Traffic']}
                  labelFormatter={(_, p) => (p?.[0]?.payload?.url ?? '')}
                />
                <Bar dataKey="traffic" fill="var(--color-coral)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Per-target tables */}
      {targets.map((target) => {
        const res = results[target];
        if (!res) return null;
        return (
          <Card key={target} className="border-0 shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{target}</span>
                {target === ownDomain && (
                  <span className="text-[10px] font-medium uppercase tracking-wide rounded bg-coral-light text-coral-dark px-1.5 py-0.5">
                    You
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {res.total_pages} pages · {res.total_traffic.toLocaleString()} visits/mo
              </span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/20">
                    <TableHead className="min-w-[240px]">Page</TableHead>
                    <TableHead className="text-right">Traffic</TableHead>
                    <TableHead className="text-right">Keywords</TableHead>
                    <TableHead className="min-w-[160px]">Top keyword</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {res.pages.slice(0, 15).map((p) => (
                    <TableRow key={p.url}>
                      <TableCell className="max-w-[320px] truncate">
                        <a
                          href={p.url.startsWith('http') ? p.url : `https://${p.url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-coral-dark hover:underline inline-flex items-center gap-1"
                        >
                          {p.url} <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{p.traffic.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.keywords.toLocaleString()}</TableCell>
                      <TableCell className="truncate">{p.top_keyword || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!p.top_keyword}
                          onClick={() => addToPipeline(p.top_keyword)}
                          className="gap-1"
                        >
                          <Plus className="h-3.5 w-3.5" /> Pipeline
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export default CompetitorTopPages;
